import React, { FunctionComponent, useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { CloseOutlined, InfoCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Input,
  Button,
  PageHeader,
  Row,
  Col,
  Upload,
  Divider,
  Form,
  Select,
  Card,
  Checkbox,
  Tooltip,
} from 'antd';
import { toast } from 'react-toastify';
import CourseService from '@services/courseService';
import { useNavigate } from '@tanstack/react-router';
import GoalsForm from '@components/forms/GoalsForm';
import ChildrenItemsForm from '@components/forms/ChildrenItemsForm';
import ProjectsForm from '@components/forms/ProjectsForm';
import { normalizeProjects } from '@components/forms/helpers';
import ApiService from '@services/apiService';
import { UploadChangeParam } from 'antd/lib/upload';
import AIIcon from '@assets/AI_with_notes.webp';
import InstructorList from '@components/instructors/InstructorList';
import { queryClient, teachurKeys, useGetCourseCategoriesQuery } from '@teachur/http-client';
import { DEGREE_LEVEL_OPTIONS } from '@new/common/constants/degreeLevels';

interface CourseFormProps {
  course: Partial<Course>;
  lessons: Lesson[];
}

const CourseForm: FunctionComponent<CourseFormProps> = ({ course, ...rest }) => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>(course.goals || []);
  const [connections, setConnections] = useState<Course[]>(course.connections || []);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [syllabusLoading, setSyllabusLoading] = useState(false);
  const [shortDesLoading, setShortDesLoading] = useState(false);
  const [longDesLoading, setLongDesLoading] = useState(false);
  const [lessons, setLessons] = useState<Lesson[]>(rest.lessons || []);
  const [projects, setProjects] = useState<Project[]>(course.projects || []);

  const [courseInfoAI, setCourseInfoAI] = useState('');
  const [shortDescription, setShortDescription] = useState(course.shortDescription);
  const [longDescription, setLongDescription] = useState(course.longDescription);

  const [image, setImage] = useState(course.image);
  const [form] = Form.useForm();
  const { setFieldsValue, validateFields } = form;
  const { data: courseCategories } = useGetCourseCategoriesQuery();

  const [goalsAICourse, setGoalsAICourse] = useState<any>([]);
  const [chaptersAICourse, setChaptersAICourse] = useState<any>([]);
  const [lessonsAICourse, setLessonsAICourse] = useState<any>([]);
  const [lessonDemoAI, setLessonDemoAI] = useState<any>([]);
  const [lessonDemoAI11, setLessonDemoAI11] = useState<any>([]);
  const [topicsAILessons, setTopicsAILessons] = useState<any>({
    prompt: {
      courseName: '',
      lessonInfo: '',
      lessonName: '',
      userIdentifier: '',
    },
    topics: [],
  });
  // const [showCourseKey, setShowCourseKey] = useState(false);
  const formValues = form.getFieldsValue();

  const courseName = useMemo(() => form.getFieldValue('name'), [formValues]);

  const generateAICourseData = async () => {
    await AICourseSyllabusGet();
    // await AICourseSortDesGet(course, goalsAICourse);
    // await AICourseLongDesGet(goalsAICourse);
  };

  const handleSubmit = (e: any) => {
    validateFields().then((values: Course) => {
      if (course.id) {
        if (!values.credit || values.credit < 0) {
          values.credit = 3;
        }
        setUpdateLoading(true);

        const normalizedProjects = projects ? normalizeProjects(projects) : [];

        const savePromise =
          course.id === -1
            ? CourseService.create({
                ...values,
                longDescription,
                credit: Number(values.credit),
                forCredit: values.forCredit ? true : null,
                lessons,
                connections,
                goals: goals as Goal[],
                image,
                projects: normalizedProjects as Project[],
              })
            : CourseService.update(course.id, {
                ...values,
                longDescription,
                credit: Number(values.credit),
                forCredit: values.forCredit ? true : null,
                lessons,
                connections,
                id: course.id,
                image,
                goals: goals as Goal[],
                projects: normalizedProjects as Project[],
              });

        savePromise
          .then((d) => {
            const successMessage = course.id === -1 ? 'Create' : 'Update';
            queryClient.setQueryData(teachurKeys.courses.lessons(d.id), lessons);
            toast.success(`${successMessage} successful!`);
            navigate({ to: `/courses/${d.id}` });
          })
          .catch((e) => {
            const message = e.response?.data?.message;

            if (message?.includes('already exists')) {
              toast.error('Course name already exists. Please choose another name.');
            } else {
              toast.error('Save failed!');
            }

            setUpdateLoading(false);
          });
      }
    });
  };

  const onUploadChange = ({ fileList, file }: UploadChangeParam) => {
    if (file.percent === 100) {
      setImage(file.response);
    }
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const handleUpdateGoals = (goals: Goal[]) => {
    setGoals(goals);
  };

  // const hideShowCourseKey = (a: any) => {
  //   setShowCourseKey(!a);
  // };

  const AICourseSyllabusGet = async () => {
    try {
      if (courseName == '' || courseName == undefined) {
        return;
      }
      const sendData = {
        courseName,
        courseInfo: courseInfoAI,
        userIdentifier: '',
      };
      setSyllabusLoading(true);
      const CourseSyllabus = await CourseService.getCourseSyllabusAI(sendData);
      if (CourseSyllabus.success) {
        setSyllabusLoading(false);
        setGoalsAICourse(CourseSyllabus.data.completion.goals);
        setChaptersAICourse(CourseSyllabus.data.completion.chapters);
        const sroreList: { id: number; text: string }[] = [];
        if (CourseSyllabus.data.completion.goals.length) {
          CourseSyllabus.data.completion.goals.map((item: any) => {
            sroreList.push({ id: -1, text: item });
          });
          // setGoals(sroreList);
          setGoals([...goals, ...sroreList]);
        }
        const sroreList1: { id: number; text: string }[] = [];
        if (CourseSyllabus.data.completion.chapters.length) {
          CourseSyllabus.data.completion.chapters.map((item: any) => {
            sroreList1.push({ id: -1, text: item });
          });
          setLessonsAICourse(sroreList1);
        }
      } else {
        setSyllabusLoading(false);
        // console.log('Get response else', CourseSyllabus);
      }
    } catch (err) {
      setSyllabusLoading(false);
      // console.log('Get response catch', err);
    }
  };

  const handleRemoveLession = (index: number) => {
    const newData = lessonsAICourse;
    if (newData[index]) {
      newData.splice(index, 1);
    }
    setLessonsAICourse([...newData]);
  };
  const handleAddLession = (index: number, lession: any) => {
    if (courseName == '' || courseName == undefined) {
      return;
    }
    if (lession.text == '' || lession.text == undefined) {
      return;
    }
    setLessonDemoAI({ courseName, lessionNameAI: lession.text });
    handleRemoveLession(index);
  };
  const AICourseSortDesGet = async (course: any, goalAI: []) => {
    try {
      if (courseName == '' || courseName == undefined) {
        return;
      }
      if (goalAI.length == 0) {
        // console.log('goalAI field is required!');
        return;
      }
      // const goalAIFilter = goalAI.toString().replace(/,/g, '\n-');
      const sendData = {
        courseName,
        courseGoals: goalAI.toString().replace(/,/g, '\n-'),
        userIdentifier: '',
      };

      setShortDesLoading(true);
      const CourseSortDes = await CourseService.getCourseSortDesAI(sendData);
      if (CourseSortDes.success) {
        setShortDesLoading(false);
        // console.log('CourseSortDes', CourseSortDes.data.completion);
        setShortDescription(CourseSortDes.data.completion);
        form.setFieldsValue({
          shortDescription: CourseSortDes.data.completion,
        });
      } else {
        // console.log('Get response else', CourseSortDes);
      }
    } catch (err) {
      // toast.error('An error occured!');
    }
  };

  const AICourseLongDesGet = async (goalAI: []) => {
    try {
      if (courseName == '' || courseName == undefined) {
        return;
      }
      if (shortDescription == '' || shortDescription == undefined) {
        return;
      }
      if (goalAI.length == 0) {
        // console.log('AI field is required!');
        return;
      }
      const sendData = {
        courseName,
        courseGoals: goalAI.toString().replace(/,/g, '\n-'),
        userIdentifier: '',
      };
      setLongDesLoading(true);
      const CourseLongDes = await CourseService.getCourseLongDesAI(sendData);
      if (CourseLongDes.success) {
        setLongDesLoading(false);
        // console.log('CourseLongDes', CourseLongDes.data.completion[0]);
        // toast.success('Student course review updated!');
        setLongDescription(CourseLongDes.data.completion[0]);
      } else {
        // console.log('Get response else', CourseLongDes);
      }
    } catch (err) {
      // toast.error('An error occured!');
    }
  };
  return (
    <Form form={form} onFinish={handleSubmit} initialValues={course} layout="vertical">
      <Row gutter={[16, { xs: 16, sm: 16 }]}>
        <Col lg={24} md={14}>
          <PageHeader
            style={{
              background: '#fff',
              padding: '24px 16px 16px',
            }}
            extra={[
              <Button key="save" type="primary" htmlType="submit">
                Save
              </Button>,
            ]}
            title={
              <>
                <h2 style={{ marginBottom: 0 }}>{course.id === -1 ? 'Create ' : 'Edit '}Course</h2>
              </>
            }
          />
          <div
            style={{
              background: '#fff',
              padding: '16px 16px',
            }}
          >
            <Form.Item
              name="visibility"
              label="Visibility"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={course.visibility || 'public'}
            >
              <Select placeholder="Select visibility">
                <Select.Option value="public">Public</Select.Option>
                <Select.Option value="private">Private</Select.Option>
                <Select.Option value="archived">Archived</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="level"
              label="Level"
              initialValue={course.id === -1 ? 'bachelor' : course.level}
            >
              <Select placeholder="Select level" allowClear>
                {DEGREE_LEVEL_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="categoryIds" label="Category" initialValue={course.categoryIds || []}>
              <Select mode="multiple" placeholder="Select category" allowClear>
                {courseCategories
                  ?.filter((category) => category.name !== 'Elective')
                  .map((category) => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="isPractical"
                  label=" "
                  colon={false}
                  valuePropName="checked"
                  initialValue={course.isPractical === true}
                >
                  <Checkbox>
                    Practical course{' '}
                    <Tooltip title="Practical courses skip the normal review flow: the course project is auto-approved a minute after submission, and the Course Hearing step is replaced with a video the student watches and confirms, which immediately completes the course.">
                      <InfoCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
              initialValue={course.name}
            >
              <Input />
            </Form.Item>
            {/* <Button
              className="ai-btn-syllabus"
              type="primary"
              size="small"
              onClick={() => {
                hideShowCourseKey(showCourseKey);
              }}
            >
              <PlusOutlined
                style={{ color: '#46a5e5', fontSize: '22px' }}
                className="key_pluse"
              />
            </Button>
            {showCourseKey && ( */}
            <Form.Item label="" initialValue="">
              <Input.TextArea
                onChange={(e: any) => {
                  setCourseInfoAI(e.target.value);
                }}
                placeholder="Enter information about your course, key words, dates, areas to be covered. This information will be used by AI assistant to generate the syllabus and long description."
                rows={4}
              />
            </Form.Item>
            <Button
              type="primary"
              ghost
              onClick={generateAICourseData}
              style={{ marginBottom: 10 }}
              disabled={syllabusLoading || !courseName || !courseInfoAI}
            >
              Generate course data using AI assistant
            </Button>
            {/* )} */}
            <Form.Item name="introVideo" label="Intro video" initialValue={course.introVideo}>
              <Input />
            </Form.Item>
            <Form.Item
              label="Image"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              initialValue={
                image
                  ? {
                      uid: image.id,
                      name: image.pid || image.url,
                      status: 'done',
                      ...image,
                    }
                  : []
              }
            >
              <>
                <div>
                  {image && image.url ? <img style={{ maxWidth: 400 }} src={image.url} /> : null}
                </div>
                <Upload
                  name="file"
                  action={ApiService.getUrl('/files')}
                  headers={ApiService.getUploadHeaders()}
                  listType="picture"
                  onChange={(e) => onUploadChange(e)}
                >
                  <Button>
                    <UploadOutlined /> Click to upload
                  </Button>
                </Upload>
              </>
            </Form.Item>
            <Button
              className="ai-btn-short-des"
              disabled={goalsAICourse.length == 0}
              type="primary"
              onClick={() => {
                AICourseSortDesGet(course, goalsAICourse);
              }}
              loading={shortDesLoading}
            >
              {!shortDesLoading && (
                <img className={shortDesLoading ? 'AIIcon-loding' : ''} src={AIIcon} />
              )}
            </Button>
            <Form.Item
              name="shortDescription"
              label="Short description"
              initialValue={course.shortDescription}
              rules={[
                {
                  required: true,
                  message: 'This field is required!',
                },
              ]}
            >
              <Input.TextArea
                onChange={(e: any) => {
                  setShortDescription(e.target.value);
                }}
              />
            </Form.Item>
            <div className="ant-col ant-form-item-label">
              <label className="ant-form-item-required" title="Long description">
                Long description
              </label>
              <span>
                <Button
                  type="primary"
                  disabled={goalsAICourse.length == 0}
                  onClick={() => {
                    AICourseLongDesGet(goalsAICourse);
                  }}
                  loading={longDesLoading}
                  className="ai-btn-long-des"
                >
                  <img src={AIIcon} />
                </Button>
              </span>
            </div>
            <ReactQuill
              value={longDescription || ''}
              onChange={(value: string) => {
                setLongDescription(value);
              }}
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="credit" label="Credit hours" initialValue={course.credit}>
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="forCredit"
                  label=" "
                  colon={false}
                  valuePropName="checked"
                  initialValue={course.id === -1 ? true : course.forCredit === true}
                >
                  <Checkbox>For credit</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Button loading={syllabusLoading} className="ai-btn-course-goals" type="primary">
              {!syllabusLoading && (
                <img
                  onClick={() => {
                    AICourseSyllabusGet();
                  }}
                  src={AIIcon}
                />
              )}
            </Button>
            <GoalsForm goals={goals} onUpdate={handleUpdateGoals} />
            <Button loading={syllabusLoading} className="ai-btn-course-lession" type="primary">
              {!syllabusLoading && (
                <img
                  onClick={() => {
                    AICourseSyllabusGet();
                  }}
                  src={AIIcon}
                />
              )}
            </Button>
            <ChildrenItemsForm<Lesson>
              type="lesson"
              items={lessons}
              setItems={setLessons}
              setFieldsValue={setFieldsValue}
              showCreate
              lessionAI={lessonDemoAI}
            />
            <div style={{ marginTop: '15px' }}>
              <ul>
                {lessonsAICourse.map((lession: any, index: any) => (
                  <li key={`${lession.id}#${index}`} style={{ marginTop: '15px' }}>
                    <>
                      {lession.text}
                      <Button
                        style={{ marginLeft: '10px', marginRight: '10px' }}
                        danger
                        size="small"
                        ghost
                        onClick={() => {
                          handleRemoveLession(index);
                        }}
                      >
                        <CloseOutlined />
                      </Button>
                      <Button
                        onClick={() => {
                          handleAddLession(index, lession);
                        }}
                        className="ai-comman-btn-pulse"
                        size="small"
                      >
                        <PlusOutlined style={{ color: '#46a5e5' }} className="key_pluse" />
                      </Button>
                    </>
                  </li>
                ))}
              </ul>
            </div>
            <Divider dashed />
            <h2>Course Connections</h2>
            <Form.Item name="code" label="Course Code" initialValue={course.code}>
              <Input />
            </Form.Item>
            <ChildrenItemsForm<Course>
              title="Prerequisites"
              type="course"
              items={connections}
              setItems={setConnections}
              setFieldsValue={setFieldsValue}
              lessionAI={lessonDemoAI11}
            />
            <Divider dashed />
            <h2>Projects</h2>
            <ProjectsForm
              topicsAILessons={topicsAILessons}
              projects={projects}
              setProjects={setProjects}
            />
            <Divider dashed />
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={updateLoading}>
                Save
              </Button>
            </Form.Item>
          </div>

          {course.id && (
            <Card
              size="small"
              title="Instructors"
              className="sidebar-widget"
              style={{ marginBottom: 16 }}
            >
              <InstructorList type="course" itemId={course.id} />
            </Card>
          )}
        </Col>
        {/* <Col lg={8} md={10}>
        <Card size="small" title="Projects" className="sidebar-widget">
          <ProjectsForm
            projects={projects}
            setProjects={setProjects}
            getFieldDecorator={getFieldDecorator}
          />
        </Card>
      </Col> */}
      </Row>
    </Form>
  );
};
export default CourseForm;
